// React
import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";

// Libraries
import { map, get, isEmpty, find, includes, orderBy, reject, filter, join, split, omit, isEqual } from "lodash";
import PropTypes from "prop-types";
import Joi from "joi";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import { Button } from "semantic-ui-react";

// Redux
import { connect } from "react-redux";
import genericAction from "@Actions/generic";
import { updateChatData } from "@Actions/chats";

// Custom Hooks
import useWindowSize from "@Hooks/useWindowSize";

// Utils
import { getValidationErrors } from "@Utils/functionality";
import { getExcludingNulls } from "@Utils";

// subComponents
import FloatingEditButton from "./SubComponents/FloatingEditButton";
import StaticFieldCard from "./SubComponents/StaticFieldCard";
import DynamicFields from "./SubComponents/DynamicFields";

// Styles
import styles from "./styles.module.css";

const CompanyFields = forwardRef(
	(
		{
			contactData,
			companyFields,
			dispatch,
			phoneEdit = true,
			chatToRefresh = {},
			setShowErrorsLabel = () => {},
			disableFloatingButton = false,
			disabledStyle = false,
			// disablePrintButton = false,
			showChannel = false,
			isHistoric,
			handlePrint,
			setCurrentContactInfo = () => {},
			noSendQuestion = false,
			questionsLanguage = "ES",
			contactFields,
			token,
			noDisableStaticFields = false,
			highlightBlocked = false,
		},
		ref
	) => {
		const defaultErrors = { name: "", phone: "", email: "" };
		const [companyFieldsData, setCompanyFieldsData] = useState({});
		const [contactInfo, setContactInfo] = useState({ name: "", phone: "", email: "", channel: "", isBlocked: false });
		const [initialFieldData, setInitialFieldData] = useState({});
		const [isEditable, setIsEditable] = useState(false);
		const [errors, setErrors] = useState(defaultErrors);

		const windowSize = useWindowSize();
		const schema = Joi.object().keys({
			name: Joi.string().min(4).messages({
				"string.min": "Nombre debe tener mínimo 3 caracteres",
				"string.empty": "Nombre es un campo requerido",
			}),
			email: Joi.string()
				.allow("")
				.email({ tlds: { allow: false } })
				.messages({
					"string.email": "Email debe ser un correo electrónico válido",
				}),
			phone: Joi.string()
				.allow("")
				.custom((value) => {
					if (!isValidPhoneNumber(value, "DO")) {
						throw new Error("");
					}
					if (isEmpty(value)) return value;
					return parsePhoneNumber(value, "DO").nationalNumber;
				}, "libphonenumber validation")
				.messages({
					"any.custom": "El teléfono no es valido",
				}),
		});

		useEffect(() => {
			refreshContactData();
			setShowErrorsLabel(false);
			setIsEditable(false);
		}, [contactData.id]);

		const refreshContactData = async () => {
			let currentContact = contactData;
			if (!currentContact) return;
			if (token) {
				const response = await dispatch(
					genericAction({
						method: "get",
						route: `contacts/profile/${token}`,
						withLoading: false,
					})
				);
				currentContact = response;
			} else if (currentContact.contactId) {
				currentContact = await dispatch(
					genericAction({
						method: "get",
						route: `contacts/${currentContact.contactId}`,
						withLoading: false,
					})
				);
			}
			let companyContactFields = [];
			if (token) {
				companyContactFields = get(currentContact, "companyContactFields") || get(currentContact, "contact.companyContactFields") || [];
			} else {
				companyContactFields = get(currentContact, "companyContactFields", []);
			}
			if (isEmpty(companyContactFields)) {
				companyContactFields = companyFields;
			}
			const allGroups = get(currentContact, "company.groups", []);
			const fieldsBase = token ? get(currentContact, "company.companyFields", []) : companyFields;
			const currentCompanyContactFields = setSaveValuesToCompanyFields(fieldsBase, companyContactFields, allGroups);
			const currentCompanyFieldData = fieldsDataFormat(currentCompanyContactFields);
			setCompanyFieldsData(currentCompanyFieldData);
			setCurrentContactInfo(currentCompanyFieldData);

			const contactInfoData = {
				name: getExcludingNulls(currentContact, "name", ""),
				phone: getExcludingNulls(currentContact, "phone", ""),
				email: getExcludingNulls(currentContact, "email", ""),
				channel: getExcludingNulls(currentContact, "contact.channel", ""),
				isBlocked: Boolean(get(currentContact, "isBlocked", false)),
			};
			setContactInfo(contactInfoData);
			setInitialFieldData({ contactInfoData, companyFieldsData: currentCompanyFieldData });
		};

		const changeCompanyFieldData = (fieldsData) => {
			return map(fieldsData, (field) => {
				return map(field.items, (item) => {
					return item.value ?? "";
				});
			});
		};

		const validChanged = () => {
			if (!isEqual(get(initialFieldData, "contactInfoData", {}), contactInfo)) {
				return true;
			}
			const originalValues = changeCompanyFieldData(get(initialFieldData, "companyFieldsData", []));
			const currentValues = changeCompanyFieldData(companyFieldsData);
			if (!isEqual(originalValues, currentValues)) {
				return true;
			}
			return false;
		};
		const setSaveValuesToCompanyFields = (dataFields, companyContactFields) => {
			const fieldsBase = Array.isArray(dataFields) ? dataFields : companyFields || [];
			if (isEmpty(companyContactFields)) return [];

			return map(fieldsBase, (field) => {
				const contactField = find(companyContactFields, { label: field.label });
				const isGroupField = field.type === "group";
				const updatedOptions = field.options;

				const updatedItems = map(field.items, (item) => {
					const contactItem = contactField ? find(contactField.items, { label: item.label }) : null;
					return {
						...item,
						value: contactItem ? contactItem.value : item.value,
						options: isGroupField ? updatedOptions : item.options,
					};
				});
				return {
					...field,
					items: updatedItems,
					value: contactField?.value || field.value || "",
					options: updatedOptions,
				};
			});
		};

		// CompanyFields handlers
		const getDefaultOption = (item) => {
			let currentValue = get(item, "value", "");
			if (includes(["number", "string"], get(item, "type", "")) && !isEmpty(get(item, "options", [])) && isEmpty(get(item, "value", ""))) {
				currentValue = `§${get(item, "options[0].name", "")}`;
			}

			return getValueFromSave({ ...item, value: currentValue });
		};

		const setIdFields = (fieldsData) => {
			const fieldsWithIds = map(fieldsData, (field, index) => {
				return { ...field, value: getDefaultOption(field), id: index + 1 };
			});
			return fieldsWithIds;
		};

		const setIdFieldsList = (fieldsData) => {
			const fieldsWithIds = map(fieldsData, (field) => {
				const fieldItems = map(field.items, (item, index) => {
					return { ...item, value: getDefaultOption(item), id: index + 1 };
				});

				return { ...field, items: orderBy(fieldItems, ["order"], ["asc"]) };
			});

			return fieldsWithIds;
		};

		const fieldsDataFormat = (fieldsData) => {
			const fieldsWithIds = setIdFields(fieldsData);
			const regularCardData = reject(fieldsWithIds, { type: "list" });
			const fieldList = filter(fieldsWithIds, { type: "list" });
			const nextCardsData = setIdFieldsList(fieldList);

			const firstCardData = {
				icon: "",
				id: 0,
				isRequired: false,
				isUpdateable: false,
				isVisible: false,
				items: orderBy(regularCardData, ["order"], ["asc"]),
				label: "Registro de Información",
				length: null,
				options: [],
				order: 1,
				type: "regular",
				value: "",
			};

			let currentCardsData = orderBy([...nextCardsData], ["order"], ["asc"]);

			if (!isEmpty(regularCardData)) {
				currentCardsData = orderBy([firstCardData, ...nextCardsData], ["order"], ["asc"]);
			}

			return currentCardsData;
		};

		const getValueToSave = (item) => {
			let currentValue = item.value;
			switch (item.type) {
				case "enum":
					currentValue = Array.isArray(item.value) ? join(item.value, ",") : item.value;
					break;

				case "direction":
					currentValue = item.value;
					break;

				default:
					currentValue = String(item.value);
			}
			return currentValue;
		};

		const getValueFromSave = (item) => {
			let currentValue = "";
			switch (item.type) {
				case "label":
				case "group":
				case "enum":
					if (item.value.indexOf(",") !== -1) {
						currentValue = split(item.value, ",").filter((option) => {
							return option.trim() !== "";
						});
					} else {
						currentValue = item.value;
					}

					break;

				case "bool":
					currentValue = item.value === "true";
					break;

				case "direction":
					currentValue = item.value;
					break;
				case "image":
					if (!isEmpty(item.value)) {
						currentValue = [item.value];
					}

					break;
				default:
					currentValue = item.value;
			}
			return currentValue;
		};

		const fieldsDataToSaveFormat = () => {
			const regularCardData = find(companyFieldsData, { type: "regular" });
			const fieldList = reject(companyFieldsData, { type: "regular" });
			const regularItems = get(regularCardData, "items", []);
			const regularItemsOmit = map(regularItems, (item) => {
				const omittedItem = omit(item, ["id", "error"]);
				const currentValue = getValueToSave(item);
				return { ...omittedItem, value: currentValue };
			});

			const fieldListOmit = map(fieldList, (field) => {
				const updatedItems = map(field.items, (item) => {
					const omittedItem = omit(item, ["id", "error"]);
					const currentValue = getValueToSave(item);
					return { ...omittedItem, value: currentValue };
				});
				return { ...omit(field, ["id", "error"]), items: updatedItems };
			});

			const dataToSave = orderBy([...regularItemsOmit, ...fieldListOmit], ["order"], ["asc"]);
			return dataToSave;
		};

		const onEnableEdit = () => {
			setIsEditable(true);
		};

		const onCancelEdit = () => {
			onContadSaveSuccess();
		};

		const companyFieldsInputHandler = ({ value, fieldCard, fieldData }) => {
			const updatedCompanyFields = map(companyFieldsData, (field) => {
				if (field.id === fieldCard.id) {
					const updatedItems = map(field.items, (item) => {
						if (item.id === fieldData.id) {
							return { ...item, value, error: "" };
						}
						return item;
					});
					return { ...field, items: updatedItems };
				}
				return field;
			});

			setCompanyFieldsData(updatedCompanyFields);
			setCurrentContactInfo(updatedCompanyFields);
		};

		const isEmptyValue = (value) => {
			if (value === null || value === undefined || value === false) {
				return true;
			}

			if (typeof value === "string") {
				if (value.trim() === "") return true;
				const indexSection = value.indexOf("§");
				if (indexSection !== -1) {
					if (indexSection === 0 || value.slice(0, indexSection).trim() === "") {
						return true;
					}
				}
				return false;
			}

			if (Array.isArray(value)) {
				return value.length === 0;
			}

			if (typeof value === "object") {
				return Object.keys(value).length === 0;
			}

			return false;
		};

		const getCompanyFieldsErrors = () => {
			let haveErrors = false;

			const updatedCompanyFields = map(companyFieldsData, (field) => {
				const updatedItems = map(field.items, (item) => {
					const currentValue = item.value;
					const separatorSymbol = "§";
					let primaryValue = "";

					if (Array.isArray(currentValue)) {
						primaryValue = currentValue;
					} else if (typeof currentValue === "string") {
						[primaryValue = ""] = currentValue ? currentValue.split(separatorSymbol) : [""];
					} else {
						primaryValue = currentValue;
					}

					// required
					if (item.isRequired && isEmptyValue(item.value)) {
						haveErrors = true;
						return { ...item, error: "Este campo es requerido" };
					}
					// minimum number of digits
					if (!isEmpty(primaryValue) && item.minDigits) {
						if (
							(Array.isArray(primaryValue) && primaryValue.length < item.minDigits) ||
							(typeof primaryValue === "string" && primaryValue.length < item.minDigits)
						) {
							haveErrors = true;
							return {
								...item,
								error: `Este campo debe tener mínimo ${item.minDigits} ${Array.isArray(primaryValue) ? "elementos" : "caracteres"}`,
							};
						}
					}

					return { ...item, error: "" };
				});
				return { ...field, items: updatedItems };
			});
			setCompanyFieldsData(updatedCompanyFields);
			setCurrentContactInfo(updatedCompanyFields);
			return haveErrors;
		};

		const inputsHandler = (label = "") => {
			switch (label) {
				case "name":
					return "Nombre";
				case "phone":
					return "Teléfono";
				case "email":
					return "Email";

				default:
					return label;
			}
		};

		const contactValidation = () => {
			let haveErrors = false;
			let currentErrors = {};
			map(contactFields, (field) => {
				const label = get(field, "label", "");
				const value = get(contactInfo, label, "");

				if (get(field, "isRequired", false) && isEmpty(value)) {
					haveErrors = true;
					currentErrors = { ...currentErrors, [label]: `${inputsHandler(label)} es un campo requerido` };
					return null;
				}
				currentErrors = { ...currentErrors, [label]: "" };
				return null;
			});

			return { haveErrors, currentErrors };
		};

		// handler save
		const onContactDataEdit = async () => {
			const validation = schema.validate(
				{ name: contactInfo?.name, email: contactInfo?.email, phone: contactInfo?.phone },
				{ abortEarly: false }
			);

			const fieldsValidations = getCompanyFieldsErrors();
			const contactInfoValidation = contactValidation ? contactValidation() : { haveErrors: false, currentErrors: {} };

			if (validation.error || fieldsValidations || contactInfoValidation.haveErrors) {
				if (validation.error) {
					setErrors(getValidationErrors(validation, defaultErrors));
				} else {
					setErrors({ ...defaultErrors, ...contactInfoValidation.currentErrors });
				}
				setShowErrorsLabel(true);
				return false;
			}

			const dataEdit = {
				name: contactInfo?.name,
				email: contactInfo?.email,
				phone: contactInfo?.phone,
				isBlocked: Boolean(contactInfo?.isBlocked),
				companyContactFields: fieldsDataToSaveFormat(),
			};

			const isExternal = Boolean(token);
			const route = isExternal ? `contacts/profile/${token}` : `contacts/${contactData.id}`;
			const method = isExternal ? "post" : "patch";

			await dispatch(
				genericAction({
					method,
					route,
					body: dataEdit,
					onSuccess: onContadSaveSuccess,
				})
			);
			return true;
		};
		const onContadSaveSuccess = (data) => {
			if (!isEmpty(data)) {
				const payload = data.data;
				setContactInfo((prev) => {
					return {
						...prev,
						name: payload?.name ?? prev.name,
						email: payload?.email ?? prev.email,
						phone: payload?.phone ?? prev.phone,
						isBlocked: Boolean(payload?.isBlocked ?? prev.isBlocked),
					};
				});
				if (!isEmpty(chatToRefresh)) {
					const updatedChat = {
						...chatToRefresh,
						contact: {
							...chatToRefresh.contact,
							email: payload.email,
							name: payload.name,
							phone: payload.phone,
							isBlocked: payload.isBlocked,
							companyContactFields: payload.companyContactFields,
						},
					};
					dispatch(updateChatData(updatedChat));
				}
			}
			setIsEditable(false);
			refreshContactData();
			setErrors(defaultErrors);
			setShowErrorsLabel(false);
		};

		// expose methods to parent component
		useImperativeHandle(ref, () => {
			return {
				save: onContactDataEdit,
				isEditable: setIsEditable,
				validChange: validChanged,
			};
		});
		const contactGroupsGruops = map(get(contactData, "company.groups", []), (group) => {
			return {
				name: group.name || "",
			};
		});

		return (
			<div className={styles.container}>
				<div style={{ position: "relative" }}>
					{/* {!disablePrintButton && isHistoric && <Button icon="print" className={styles.printButton} onClick={handlePrint} />} */}
					{!disableFloatingButton && !isHistoric && (
						<>
							<Button className={styles.printButton} icon="print" onClick={handlePrint} />
							<FloatingEditButton
								isEditing={isEditable}
								onEdit={onEnableEdit}
								onSave={onContactDataEdit}
								onCancel={onCancelEdit}
								handlePrint={handlePrint}
								position={{ top: "0.75rem", right: "1rem" }}
							/>
						</>
					)}
					<StaticFieldCard
						isEditable={isEditable}
						contactInfo={contactInfo}
						setContactInfo={setContactInfo}
						errors={errors}
						phoneEdit={phoneEdit}
						showChannel={showChannel}
						disabledStyle={disabledStyle}
						contactFields={contactFields}
						contactData={contactData}
						noDisableStaticFields={noDisableStaticFields}
						highlightBlocked={highlightBlocked}
					/>
				</div>
				{!isEmpty(companyFieldsData) && (
					<div className={styles.infoContainer}>
						<DynamicFields
							isEditable={isEditable}
							companyFieldsData={companyFieldsData}
							companyFieldsInputHandler={companyFieldsInputHandler}
							noSendQuestion={noSendQuestion}
							questionsLanguage={questionsLanguage}
							windowSize={windowSize}
							disabledStyle={disabledStyle}
							groupsToken={contactGroupsGruops}
							token={token}
						/>
					</div>
				)}
			</div>
		);
	}
);

CompanyFields.propTypes = {
	dispatch: PropTypes.func,
	handlePrint: PropTypes.func,
	setShowErrorsLabel: PropTypes.func,
	setCurrentContactInfo: PropTypes.func,
	companyFields: PropTypes.array,
	isHistoric: PropTypes.bool,
	contactData: PropTypes.object,
	chatToRefresh: PropTypes.object,
	phoneEdit: PropTypes.bool,
	disableFloatingButton: PropTypes.bool,
	disabledStyle: PropTypes.bool,
	// disablePrintButton: PropTypes.bool,
	showChannel: PropTypes.bool,
	noSendQuestion: PropTypes.bool,
	questionsLanguage: PropTypes.string,
	contactFields: PropTypes.array,
	token: PropTypes.string,
	noDisableStaticFields: PropTypes.bool,
	highlightBlocked: PropTypes.bool,
};

function mapStateToProps({ company, user }) {
	return {
		companyName: company.name,
		userName: user.name,
		companyFields: company.companyFields,
		contactFields: get(company, "settings.fixedFields", []),
	};
}
export default connect(mapStateToProps, null, null, { forwardRef: true })(CompanyFields);
